import { useConnector } from 'react-instantsearch';

/**
 * Rango numérico "headless" para el listado de flats.
 *
 * Aplica exactamente los mismos `numericFilters` que el histórico
 * `<Configure numericFilters>` (`importeVenta`/`metrosComputablesUA` con
 * `>=` / `<=`), pero expone su estado como uiState `range.<attr>` para que
 * el `routing` de InstantSearch lo serialice a la URL (dos vías).
 *
 * Diferencia clave con `connectRange`: NO registra el atributo como
 * `disjunctiveFacet`. `connectRange.getWidgetSearchParameters` llama
 * incondicionalmente a `addDisjunctiveFacet(attribute)`, lo que dispara una
 * petición de facets a Algolia y devuelve 400 si el atributo no está en
 * `attributesForFaceting`. `importeVenta`/`importeRecibofinal`/
 * `metrosComputablesUA` sólo son numéricos filtrables (no facetados), así
 * que este conector se queda en `addNumericRefinement` y no pide stats.
 */

const $$type = 'nestar.numericRangeFilter';

const isNum = (v: unknown): v is number =>
  v !== '' && v !== null && v !== undefined && Number.isFinite(Number(v));

/** Refinamiento actual: `[min, max]`; `undefined` en un lado = sin límite. */
export type NumericRange = [number | undefined, number | undefined];

export type NumericRangeFilterRenderState = {
  attribute: string;
  start: NumericRange;
  /** Fija el rango. Pasa `undefined` en un lado para quitar ese límite. */
  refine: (next: NumericRange) => void;
};

type WidgetParams = { attribute: string };

const connectNumericRangeFilter =
  (
    renderFn: (state: any, isFirstRender: boolean) => void,
    unmountFn: () => void = () => {},
  ) =>
  (widgetParams: WidgetParams) => {
    const { attribute } = widgetParams || ({} as WidgetParams);
    if (!attribute) {
      throw new Error('useNumericRangeFilter: `attribute` is required.');
    }

    const getCurrent = (helper: any): NumericRange => {
      const [min] = helper.getNumericRefinement(attribute, '>=') || [];
      const [max] = helper.getNumericRefinement(attribute, '<=') || [];
      return [isNum(min) ? min : undefined, isNum(max) ? max : undefined];
    };

    const makeRefine =
      (helper: any) =>
      ([nextMin, nextMax]: NumericRange) => {
        let state = helper.state.removeNumericRefinement(attribute);
        if (isNum(nextMin)) {
          state = state.addNumericRefinement(attribute, '>=', nextMin);
        }
        if (isNum(nextMax)) {
          state = state.addNumericRefinement(attribute, '<=', nextMax);
        }
        helper.setState(state.resetPage()).search();
      };

    return {
      $$type,
      init(initOptions: any) {
        renderFn(
          {
            ...this.getWidgetRenderState(initOptions),
            instantSearchInstance: initOptions.instantSearchInstance,
          },
          true,
        );
      },
      render(renderOptions: any) {
        renderFn(
          {
            ...this.getWidgetRenderState(renderOptions),
            instantSearchInstance: renderOptions.instantSearchInstance,
          },
          false,
        );
      },
      getRenderState(renderState: any, renderOptions: any) {
        return {
          ...renderState,
          numericRangeFilter: {
            ...renderState.numericRangeFilter,
            [attribute]: this.getWidgetRenderState(renderOptions),
          },
        };
      },
      getWidgetRenderState({ helper }: any) {
        return {
          attribute,
          start: getCurrent(helper),
          refine: makeRefine(helper),
          widgetParams,
        };
      },
      dispose({ state }: any) {
        unmountFn();
        return state.removeNumericRefinement(attribute);
      },
      getWidgetUiState(uiState: any, { searchParameters }: any) {
        const refs = searchParameters.getNumericRefinements(attribute);
        const min = refs['>='] || [];
        const max = refs['<='] || [];
        if (min.length === 0 && max.length === 0) return uiState;
        return {
          ...uiState,
          range: { ...uiState.range, [attribute]: `${min}:${max}` },
        };
      },
      getWidgetSearchParameters(searchParameters: any, { uiState }: any) {
        // Resetea SÓLO el refinamiento de este atributo (no toca los de
        // otros widgets) y aplica el de la URL. Sin `addDisjunctiveFacet`.
        let sp = searchParameters.setQueryParameters({
          numericRefinements: {
            ...searchParameters.numericRefinements,
            [attribute]: {},
          },
        });
        const value = uiState.range && uiState.range[attribute];
        if (!value || String(value).indexOf(':') === -1) return sp;
        const [lower, upper] = String(value).split(':').map(parseFloat);
        if (isNum(lower)) sp = sp.addNumericRefinement(attribute, '>=', lower);
        if (isNum(upper)) sp = sp.addNumericRefinement(attribute, '<=', upper);
        return sp;
      },
    };
  };

export const useNumericRangeFilter = (
  props: WidgetParams,
): NumericRangeFilterRenderState =>
  useConnector(
    connectNumericRangeFilter as any,
    props,
  ) as NumericRangeFilterRenderState;
